import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Select, Spinner, Text,
  Badge, Container, VStack, useToast, Button, HStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, EmailIcon, ViewIcon } from '@chakra-ui/icons';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

export default function LiveAttendanceReport() {
  const { user } = useContext(AuthContext);
  const toast = useToast();
  
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);

  // Timeline Modal State
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [selectedStudentTimeline, setSelectedStudentTimeline] = useState(null);

  // Fetch available meetings
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const response = await axiosInstance.get('/live-attendance/meetings');
        setMeetings(response.data); // now an array of { meetingId, roomName }
      } catch (error) {
        toast({
          title: 'خطأ في تحميل الحصص',
          description: error.response?.data?.error || error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };
    fetchMeetings();
  }, [toast]);

  // Fetch report when meeting is selected
  useEffect(() => {
    if (!selectedMeeting) {
      setReportData([]);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/live-attendance/report/${selectedMeeting}`);
        setReportData(response.data);
      } catch (error) {
        toast({
          title: 'خطأ في تحميل التقرير',
          description: error.response?.data?.error || error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [selectedMeeting, toast]);

  const handleSendSMS = async () => {
    if (!selectedMeeting) return;
    setSmsLoading(true);
    try {
      const response = await axiosInstance.post(`/live-attendance/send-absent-sms/${selectedMeeting}`);
      toast({
        title: 'تم الإرسال بنجاح',
        description: `تم وضع ${response.data.count} رسالة في طابور الإرسال للطلاب الغائبين لأكثر من 15 دقيقة.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'خطأ في إرسال الرسائل',
        description: error.response?.data?.error || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSmsLoading(false);
    }
  };

  const openTimeline = (record) => {
    setSelectedStudentTimeline(record);
    setIsTimelineOpen(true);
  };

  const formatDuration = (seconds) => {
    const totalMinutes = Math.floor(seconds / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const s = seconds % 60;
    if (h > 0) {
      return `${h}س ${m}د ${s}ث`;
    }
    return `${m}د ${s}ث`;
  };

  return (
    <Container maxW="container.xl" py={8} dir="rtl">
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg" color="blue.600">
            تقرير حضور الحصص المباشرة
          </Heading>
          {selectedMeeting && reportData.length > 0 && (
            <Button 
              colorScheme="red" 
              leftIcon={<EmailIcon />} 
              onClick={handleSendSMS}
              isLoading={smsLoading}
            >
              إرسال رسائل غياب (أكثر من 15 دقيقة)
            </Button>
          )}
        </HStack>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Text mb={2} fontWeight="bold">اختر الحصة المباشرة:</Text>
          <Select 
            placeholder="-- اختر الحصة --" 
            value={selectedMeeting} 
            onChange={(e) => setSelectedMeeting(e.target.value)}
          >
            {meetings.map((meeting) => (
              <option key={meeting.meetingId} value={meeting.meetingId}>
                {meeting.roomName}
              </option>
            ))}
          </Select>
        </Box>

        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" />
          </Box>
        ) : selectedMeeting && reportData.length > 0 ? (
          <Box bg="white" borderRadius="lg" boxShadow="sm" overflowX="auto">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>اسم الطالب</Th>
                  <Th>رقم الهاتف</Th>
                  <Th>الوقت الإجمالي</Th>
                  <Th>وقت الحضور</Th>
                  <Th>وقت الغياب</Th>
                  <Th>الحالة</Th>
                  <Th>المخطط الزمني</Th>
                </Tr>
              </Thead>
              <Tbody>
                {reportData.map((record) => {
                  const presentPercent = record.totalDuration > 0 
                    ? (record.presentDuration / record.totalDuration) * 100 
                    : 0;
                  
                  const student = record.studentId || {};
                  const phone = student.phoneNumber || student.parentPhoneNumber || student.whatsappNumberParent || 'غير متوفر';

                  return (
                    <Tr key={record._id}>
                      <Td fontWeight="bold">{student.username || 'طالب غير معروف'}</Td>
                      <Td dir="ltr" textAlign="right">{phone}</Td>
                      <Td>{formatDuration(record.totalDuration)}</Td>
                      <Td color="green.600" fontWeight="bold">{formatDuration(record.presentDuration)}</Td>
                      <Td color="red.500" fontWeight="bold">{formatDuration(record.absentDuration)}</Td>
                      <Td>
                        {presentPercent >= 80 ? (
                          <Badge colorScheme="green" px={2} py={1} borderRadius="md"><CheckCircleIcon mr={1}/> حضور ممتاز</Badge>
                        ) : presentPercent >= 50 ? (
                          <Badge colorScheme="yellow" px={2} py={1} borderRadius="md"><WarningIcon mr={1}/> حضور جزئي</Badge>
                        ) : (
                          <Badge colorScheme="red" px={2} py={1} borderRadius="md"><WarningIcon mr={1}/> غياب مستمر</Badge>
                        )}
                      </Td>
                      <Td>
                        <Button size="sm" colorScheme="blue" variant="outline" leftIcon={<ViewIcon />} onClick={() => openTimeline(record)}>
                          عرض المخطط
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        ) : selectedMeeting ? (
          <Box textAlign="center" py={10} color="gray.500">
            لا توجد سجلات حضور لهذه الحصة.
          </Box>
        ) : null}
      </VStack>

      {/* Timeline Modal */}
      <Modal isOpen={isTimelineOpen} onClose={() => setIsTimelineOpen(false)} size="3xl" isCentered motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent dir="rtl" borderRadius="xl" overflow="hidden" boxShadow="2xl">
          <ModalHeader bg="blue.600" color="white" borderBottomWidth="1px">
            المخطط الزمني للحضور والغياب
          </ModalHeader>
          <ModalCloseButton color="white" left="4" right="auto" top="3" />
          <ModalBody pb={6} pt={6} bg="gray.50">
            {selectedStudentTimeline && (
              <Box>
                <HStack justify="space-between" mb={6} bg="white" p={4} borderRadius="lg" boxShadow="sm">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">اسم الطالب</Text>
                    <Text fontWeight="bold" fontSize="lg" color="gray.800">
                      {selectedStudentTimeline.studentId?.username || 'غير معروف'}
                    </Text>
                  </VStack>
                  <VStack align="end" spacing={1}>
                    <Text fontSize="sm" color="gray.500">إجمالي وقت الحصة</Text>
                    <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
                      {formatDuration(selectedStudentTimeline.totalDuration)}
                    </Badge>
                  </VStack>
                </HStack>
                
                {(!selectedStudentTimeline.timeline || selectedStudentTimeline.timeline.length === 0) ? (
                  <Box textAlign="center" py={10} bg="white" borderRadius="lg" border="1px dashed" borderColor="gray.300">
                    <WarningIcon boxSize={10} color="orange.300" mb={4} />
                    <Text color="gray.600" fontSize="lg">لا يوجد بيانات تخطيط زمني مفصلة لهذا الطالب.</Text>
                    <Text color="gray.400" fontSize="sm" mt={2}>
                      السجلات القديمة أو تلك التي لم تكتمل بشكل صحيح لن تظهر هنا. يرجى تجربة حصة جديدة بعد التحديث.
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={8} align="stretch">
                    {/* Visual Animated Timeline Bar */}
                    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                      <Text fontWeight="bold" mb={4} color="gray.700">الشريط الزمني المرئي</Text>
                      
                      {/* CSS Animation for sliding in */}
                      <style>
                        {`
                          @keyframes expandWidth {
                            from { width: 0%; opacity: 0; }
                            to { opacity: 1; }
                          }
                          .animated-bar {
                            animation: expandWidth 1s cubic-bezier(0.25, 1, 0.5, 1) forwards;
                            transform-origin: right; /* RTL */
                          }
                        `}
                      </style>

                      <Box w="100%" h="40px" bg="gray.200" borderRadius="full" position="relative" overflow="hidden" display="flex" boxShadow="inner">
                        {selectedStudentTimeline.timeline.map((event, index, arr) => {
                          if (index === arr.length - 1) return null;
                          
                          const startTime = new Date(event.timestamp);
                          const endTime = new Date(arr[index + 1].timestamp);
                          const durationSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
                          
                          const totalMeetingSeconds = selectedStudentTimeline.totalDuration || 1;
                          const widthPercent = Math.max((durationSeconds / totalMeetingSeconds) * 100, 1);
                          
                          const isPresent = event.status === 'present';
                          const color = isPresent ? 'linear-gradient(90deg, #48BB78 0%, #38A169 100%)' : 'linear-gradient(90deg, #F56565 0%, #E53E3E 100%)';
                          
                          const timeString = `${startTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn', hour: '2-digit', minute:'2-digit', second:'2-digit' })} إلى ${endTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn', hour: '2-digit', minute:'2-digit', second:'2-digit' })}`;

                          return (
                            <Box 
                              key={index}
                              h="100%"
                              className="animated-bar"
                              style={{ width: `${widthPercent}%`, background: color }}
                              borderRight="2px solid white"
                              _last={{ borderRight: "none" }}
                              _hover={{ filter: "brightness(1.1)", cursor: "pointer" }}
                              transition="all 0.2s"
                              title={`${isPresent ? 'حاضر' : 'غائب'}: من ${timeString} (${formatDuration(durationSeconds)})`}
                            />
                          );
                        })}
                      </Box>
                      
                      <HStack mt={5} justify="center" spacing={8}>
                        <HStack><Box w="15px" h="15px" bg="#38A169" borderRadius="full" boxShadow="sm"/><Text fontWeight="medium" color="gray.700">فترة حضور</Text></HStack>
                        <HStack><Box w="15px" h="15px" bg="#E53E3E" borderRadius="full" boxShadow="sm"/><Text fontWeight="medium" color="gray.700">فترة غياب</Text></HStack>
                      </HStack>
                    </Box>

                    {/* Detailed Event Log */}
                    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                      <Text fontWeight="bold" mb={4} color="gray.700">سجل الأحداث التفصيلي</Text>
                      <VStack align="stretch" spacing={4} pl={2} pr={2}>
                        {selectedStudentTimeline.timeline.map((event, index, arr) => {
                          if (index === arr.length - 1) return null; // Skip "end" log

                          const startTime = new Date(event.timestamp);
                          const endTime = new Date(arr[index + 1].timestamp);
                          const durationSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
                          const isPresent = event.status === 'present';

                          return (
                            <HStack key={index} spacing={4} position="relative">
                              {/* Connector Line */}
                              {index !== arr.length - 2 && (
                                <Box position="absolute" right="19px" top="30px" w="2px" h="100%" bg="gray.200" zIndex={0} />
                              )}
                              
                              <Box zIndex={1} bg="white" borderRadius="full">
                                {isPresent ? (
                                  <CheckCircleIcon boxSize={10} color="green.400" bg="green.50" borderRadius="full" p={2} />
                                ) : (
                                  <WarningIcon boxSize={10} color="red.400" bg="red.50" borderRadius="full" p={2} />
                                )}
                              </Box>
                              
                              <Box flex="1" bg={isPresent ? "green.50" : "red.50"} p={3} borderRadius="md" borderWidth="1px" borderColor={isPresent ? "green.100" : "red.100"}>
                                <HStack justify="space-between">
                                  <Text fontWeight="bold" color={isPresent ? "green.700" : "red.700"}>
                                    {isPresent ? 'تم تسجيل حضور' : 'تم تسجيل غياب'}
                                  </Text>
                                  <Badge colorScheme={isPresent ? "green" : "red"} variant="subtle">
                                    {formatDuration(durationSeconds)}
                                  </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.600" mt={1}>
                                  بدأ في {startTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn' })} وانتهى في {endTime.toLocaleTimeString('ar-EG', { numberingSystem: 'latn' })}
                                </Text>
                              </Box>
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>

                  </VStack>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter bg="white" borderTopWidth="1px">
            <Button onClick={() => setIsTimelineOpen(false)} colorScheme="gray" px={8}>
              إغلاق
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Container>
  );
}
